import { useState, useEffect, useMemo, forwardRef, useImperativeHandle } from "react";
import { motion } from "framer-motion";
import { Filter, Search, Sparkles, Globe, Plus, ChevronDown, Radio, Clock, CheckCircle2, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import EventCard from "./EventCard";
import CreateEventModal from "./CreateEventModal";
import EventDetailModal from "./EventDetailModal";
import ResolveEventModal from "./ResolveEventModal";
import SubmitResolutionModal from "./SubmitResolutionModal";
import VoteOnResolutionModal from "./VoteOnResolutionModal";
import { ConfirmDialog } from "./ConfirmDialog";
import { EventGridSkeleton } from "./ui/skeleton-card";
import { useLocale } from "@/contexts/LocaleContext";

type EventLanguage = "en" | "pt" | "es" | "fr" | "de" | "it";

const eventLanguageConfigs = [
  { language: "en" as EventLanguage, flag: "🇺🇸", name: { en: "English", pt: "Inglês", es: "Inglés", fr: "Anglais", de: "Englisch", it: "Inglese" } },
  { language: "pt" as EventLanguage, flag: "🇧🇷", name: { en: "Portuguese", pt: "Português", es: "Portugués", fr: "Portugais", de: "Portugiesisch", it: "Portoghese" } },
  { language: "es" as EventLanguage, flag: "🇪🇸", name: { en: "Spanish", pt: "Espanhol", es: "Español", fr: "Espagnol", de: "Spanisch", it: "Spagnolo" } },
  { language: "fr" as EventLanguage, flag: "🇫🇷", name: { en: "French", pt: "Francês", es: "Francés", fr: "Français", de: "Französisch", it: "Francese" } },
  { language: "de" as EventLanguage, flag: "🇩🇪", name: { en: "German", pt: "Alemão", es: "Alemán", fr: "Allemand", de: "Deutsch", it: "Tedesco" } },
  { language: "it" as EventLanguage, flag: "🇮🇹", name: { en: "Italian", pt: "Italiano", es: "Italiano", fr: "Italien", de: "Italienisch", it: "Italiano" } },
  { language: "global" as EventLanguage, flag: "🌍", name: { en: "Global", pt: "Global", es: "Global", fr: "Global", de: "Global", it: "Globale" } },
];
import { useAuth } from "@/hooks/useAuth";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { useDebounce } from "@/hooks/useDebounce";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";

const EVENTS_PER_PAGE = 9;

interface Event {
  id: string;
  title: string;
  title_pt: string | null;
  description: string;
  description_pt: string | null;
  category: string;
  country: string;
  deadline: string;
  yes_pool: number;
  no_pool: number;
  total_bids: number;
  status: string;
  creator_id: string;
  is_platform_event: boolean;
}

type Category = "all" | "sports" | "crypto" | "politics" | "tech" | "entertainment" | "other";
type EventStatus = "all" | "OPEN" | "CLOSED" | "RESOLVED";

interface PendingResolution {
  id: string;
  event_id: string;
  proposed_outcome: boolean;
  dispute_deadline: string | null;
  dispute_count: number;
}

export interface EventsSectionHandle {
  openCreateModal: () => void;
}

const EventsSection = forwardRef<EventsSectionHandle, {}>((props, ref) => {
  const { t, locale } = useLocale();
  const { user } = useAuth();
  const { isAdmin } = useIsAdmin();
  const navigate = useNavigate();
  const [selectedLanguage, setSelectedLanguage] = useState<EventLanguage | "all">("all");
  const [selectedCategory, setSelectedCategory] = useState<Category>("all");
  const [selectedStatus, setSelectedStatus] = useState<EventStatus>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebounce(searchQuery, 300);
  const [events, setEvents] = useState<Event[]>([]);
  const [pendingResolutionEventIds, setPendingResolutionEventIds] = useState<Set<string>>(new Set());
  const [pendingResolutionsMap, setPendingResolutionsMap] = useState<Record<string, PendingResolution>>({});
  const [userBidEventIds, setUserBidEventIds] = useState<Set<string>>(new Set());
  const [userBidDetailsMap, setUserBidDetailsMap] = useState<Record<string, { amount: number; prediction: boolean }>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [isBidModalOpen, setIsBidModalOpen] = useState(false);
  const [isResolveModalOpen, setIsResolveModalOpen] = useState(false);
  const [isSubmitResolutionModalOpen, setIsSubmitResolutionModalOpen] = useState(false);
  const [isVoteModalOpen, setIsVoteModalOpen] = useState(false);
  const [selectedResolution, setSelectedResolution] = useState<PendingResolution | null>(null);
  const [deletingEventId, setDeletingEventId] = useState<string | null>(null);
  const [userBidsMap, setUserBidsMap] = useState<Record<string, boolean>>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [confirmDeleteEvent, setConfirmDeleteEvent] = useState<Event | null>(null);

  // Expose openCreateModal to parent via ref
  useImperativeHandle(ref, () => ({
    openCreateModal: () => setIsCreateModalOpen(true),
  }));

  const fetchEvents = async () => {
    setIsLoading(true);
    
    // Build events query
    let eventsQuery = supabase.from("events").select("*");

    if (selectedLanguage !== "all") {
      eventsQuery = eventsQuery.eq("country", selectedLanguage as any);
    }
    if (selectedCategory !== "all") {
      eventsQuery = eventsQuery.eq("category", selectedCategory);
    }
    if (selectedStatus !== "all") {
      eventsQuery = eventsQuery.eq("status", selectedStatus);
    }
    if (debouncedSearch) {
      eventsQuery = eventsQuery.or(`title.ilike.%${debouncedSearch}%,description.ilike.%${debouncedSearch}%`);
    }

    // Prepare parallel queries
    const pendingResolutionsQuery = supabase
      .from("pending_resolutions")
      .select("id, event_id, proposed_outcome, dispute_deadline, dispute_count")
      .eq("status", "pending");

    const userBidsQuery = user
      ? supabase.from("bids").select("event_id, amount, prediction").eq("user_id", user.id)
      : Promise.resolve({ data: null });

    // Execute all queries in parallel
    const [eventsResult, pendingResult, userBidsResult] = await Promise.all([
      eventsQuery,
      pendingResolutionsQuery,
      userBidsQuery,
    ]);

    const { data: eventsData, error: eventsError } = eventsResult;
    const { data: pendingData } = pendingResult;
    const { data: userBids } = userBidsResult;

    if (!eventsError && eventsData) {
      setEvents(eventsData);

      // Process pending resolutions
      if (pendingData) {
        setPendingResolutionEventIds(new Set(pendingData.map(p => p.event_id)));
        const resolutionsMap: Record<string, PendingResolution> = {};
        pendingData.forEach(p => {
          resolutionsMap[p.event_id] = p;
        });
        setPendingResolutionsMap(resolutionsMap);
      }

      // Process user bids - store both IDs and details
      if (userBids) {
        setUserBidEventIds(new Set(userBids.map(b => b.event_id)));
        // Store bid details for pre-fetching in modal
        const bidDetails: Record<string, { amount: number; prediction: boolean }> = {};
        userBids.forEach(b => {
          bidDetails[b.event_id] = { amount: Number(b.amount), prediction: b.prediction };
        });
        setUserBidDetailsMap(bidDetails);
      }

      // Batch check: get all event IDs created by user
      if (user && eventsData) {
        const userCreatedEventIds = eventsData
          .filter(e => e.creator_id === user.id)
          .map(e => e.id);

        if (userCreatedEventIds.length > 0) {
          // Single query to get all bids from other users on user-created events
          const { data: otherUsersBids } = await supabase
            .from("bids")
            .select("event_id")
            .in("event_id", userCreatedEventIds)
            .neq("user_id", user.id);

          // Build a set of events that have other users' bids
          const eventsWithOtherBids = new Set(otherUsersBids?.map(b => b.event_id) || []);
          
          // Map: true = no other users' bids (can delete), false = has other bids
          const bidsCheckMap: Record<string, boolean> = {};
          userCreatedEventIds.forEach(eventId => {
            bidsCheckMap[eventId] = !eventsWithOtherBids.has(eventId);
          });
          setUserBidsMap(bidsCheckMap);
        } else {
          setUserBidsMap({});
        }
      }
    }

    setIsLoading(false);
  };

  useEffect(() => {
    fetchEvents();

    // Subscribe to realtime updates - optimized to update single events when possible
    const channel = supabase
      .channel("events-changes")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "events" },
        (payload) => {
          const newEvent = payload.new as Event;
          // Only add if it matches current filters
          const matchesLanguage = selectedLanguage === "all" || newEvent.country === selectedLanguage;
          const matchesCategory = selectedCategory === "all" || newEvent.category === selectedCategory;
          const matchesStatus = selectedStatus === "all" || newEvent.status === selectedStatus;
          const matchesSearch = !debouncedSearch || 
            newEvent.title.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
            newEvent.description.toLowerCase().includes(debouncedSearch.toLowerCase());
          
          if (matchesLanguage && matchesCategory && matchesStatus && matchesSearch) {
            setEvents(prev => [newEvent, ...prev]);
          }
        }
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "events" },
        (payload) => {
          const updatedEvent = payload.new as Event;
          setEvents(prev => prev.map(e => e.id === updatedEvent.id ? updatedEvent : e));
        }
      )
      .on(
        "postgres_changes",
        { event: "DELETE", schema: "public", table: "events" },
        (payload) => {
          const deletedId = (payload.old as { id: string }).id;
          setEvents(prev => prev.filter(e => e.id !== deletedId));
        }
      )
      .subscribe((status) => {
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          // Connection failed - app will still work via polling/refresh
        }
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [selectedLanguage, selectedCategory, selectedStatus, debouncedSearch]);

  // Smart sorting: OPEN events by popularity, others by deadline
  const sortedEvents = useMemo(() => {
    return [...events].sort((a, b) => {
      // OPEN events first, sorted by popularity
      if (a.status === "OPEN" && b.status === "OPEN") {
        const aPopularity = a.total_bids + a.yes_pool + a.no_pool;
        const bPopularity = b.total_bids + b.yes_pool + b.no_pool;
        return bPopularity - aPopularity;
      }
      // OPEN events before closed/resolved
      if (a.status === "OPEN" && b.status !== "OPEN") return -1;
      if (a.status !== "OPEN" && b.status === "OPEN") return 1;
      // Non-open events sorted by deadline (most recent first)
      return new Date(b.deadline).getTime() - new Date(a.deadline).getTime();
    });
  }, [events]);

  // Pagination
  const totalPages = Math.ceil(sortedEvents.length / EVENTS_PER_PAGE);
  const paginatedEvents = useMemo(() => {
    const start = (currentPage - 1) * EVENTS_PER_PAGE;
    return sortedEvents.slice(start, start + EVENTS_PER_PAGE);
  }, [sortedEvents, currentPage]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedLanguage, selectedCategory, selectedStatus, debouncedSearch]);

  // Count events by status for tab badges
  const statusCounts = useMemo(() => {
    return {
      all: events.length,
      OPEN: events.filter(e => e.status === "OPEN").length,
      CLOSED: events.filter(e => e.status === "CLOSED").length,
      RESOLVED: events.filter(e => e.status === "RESOLVED").length,
    };
  }, [events]);

  const handleBidClick = (event: Event) => {
    setSelectedEvent(event);
    setIsBidModalOpen(true);
  };

  const handleCreateClick = () => {
    if (!user) {
      navigate("/auth");
      return;
    }
    setIsCreateModalOpen(true);
  };

  const handleResolveClick = (event: Event) => {
    setSelectedEvent(event);
    // Admins can directly resolve any event
    // Non-platform event creators must submit for approval
    if (isAdmin) {
      setIsResolveModalOpen(true);
    } else {
      setIsSubmitResolutionModalOpen(true);
    }
  };

  const handleVoteClick = (event: Event) => {
    const resolution = pendingResolutionsMap[event.id];
    if (resolution) {
      setSelectedEvent(event);
      setSelectedResolution(resolution);
      setIsVoteModalOpen(true);
    }
  };

  const handleDeleteClick = (event: Event) => {
    setConfirmDeleteEvent(event);
  };

  const confirmDelete = async () => {
    if (!confirmDeleteEvent) return;
    const event = confirmDeleteEvent;
    
    setDeletingEventId(event.id);
    setConfirmDeleteEvent(null);

    try {
      // Delete related data first (for creators without admin role)
      if (!isAdmin) {
        // Delete own bids first
        await supabase.from("bids").delete().eq("event_id", event.id).eq("user_id", user?.id);
      } else {
        // Admins: First refund all bids to users before deleting
        const { data: allBids } = await supabase
          .from("bids")
          .select("user_id, amount")
          .eq("event_id", event.id);

        if (allBids && allBids.length > 0) {
          // Group bids by user and sum amounts for refund
          const refundMap: Record<string, number> = {};
          allBids.forEach(bid => {
            refundMap[bid.user_id] = (refundMap[bid.user_id] || 0) + Number(bid.amount);
          });

          // Refund each user by updating their balance directly
          for (const [userId, totalAmount] of Object.entries(refundMap)) {
            const { data: profile } = await supabase
              .from("profiles")
              .select("duv_coins")
              .eq("user_id", userId)
              .single();
            
            if (profile) {
              await supabase
                .from("profiles")
                .update({ duv_coins: Number(profile.duv_coins) + totalAmount })
                .eq("user_id", userId);
            }
          }
        }

        // Get pending resolution IDs first for resolution_votes cleanup
        const { data: pendingRes } = await supabase
          .from("pending_resolutions")
          .select("id")
          .eq("event_id", event.id);

        if (pendingRes && pendingRes.length > 0) {
          for (const res of pendingRes) {
            await supabase.from("resolution_votes").delete().eq("resolution_id", res.id);
          }
        }

        // Delete all related data
        await supabase.from("bids").delete().eq("event_id", event.id);
        await supabase.from("pending_resolutions").delete().eq("event_id", event.id);
        await supabase.from("event_invites").delete().eq("event_id", event.id);
        await supabase.from("event_participants").delete().eq("event_id", event.id);
      }

      // Delete the event
      const { error } = await supabase.from("events").delete().eq("id", event.id);

      if (error) throw error;

      // Refresh events list
      fetchEvents();
    } catch (error: any) {
      console.error("Error deleting event:", error);
    } finally {
      setDeletingEventId(null);
    }
  };

  const categories: { key: Category; labelKey: string }[] = [
    { key: "all", labelKey: "events.all" },
    { key: "sports", labelKey: "events.sports" },
    { key: "crypto", labelKey: "events.crypto" },
    { key: "politics", labelKey: "events.politics" },
    { key: "tech", labelKey: "events.tech" },
    { key: "entertainment", labelKey: "events.entertainment" },
  ];

  return (
    <section id="events" className="py-24 relative">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/30 text-primary text-sm font-medium mb-4">
            <Sparkles size={16} />
            {t("events.badge")}
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
            {t("events.title")}
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            {t("events.description")}
          </p>
        </motion.div>

        {/* Search Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.05 }}
          className="max-w-xl mx-auto mb-6"
        >
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder={t("events.searchPlaceholder")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-card border-border"
            />
          </div>
        </motion.div>

        {/* Status Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.08 }}
          className="flex justify-center mb-6"
        >
          <Tabs value={selectedStatus} onValueChange={(v) => setSelectedStatus(v as EventStatus)} className="w-full max-w-lg">
            <TabsList className="grid w-full grid-cols-4 bg-card border border-border">
              <TabsTrigger value="all" className="gap-1 text-xs sm:text-sm">
                {t("events.statusAll")}
              </TabsTrigger>
              <TabsTrigger value="OPEN" className="gap-1 text-xs sm:text-sm">
                <Radio size={14} className="text-green-500" />
                {t("events.statusLive")}
              </TabsTrigger>
              <TabsTrigger value="CLOSED" className="gap-1 text-xs sm:text-sm">
                <Clock size={14} className="text-yellow-500" />
                {t("events.statusEnded")}
              </TabsTrigger>
              <TabsTrigger value="RESOLVED" className="gap-1 text-xs sm:text-sm">
                <CheckCircle2 size={14} className="text-blue-500" />
                {t("events.statusResolved")}
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="flex flex-col gap-4 mb-8"
        >
          {/* Create Event Button - Top */}
          <div className="flex justify-center mb-4">
            <Button
              variant="hero"
              size="lg"
              onClick={handleCreateClick}
              className="gap-2 px-8 py-6 text-lg font-semibold"
            >
              <Plus size={20} />
              {locale.language === "pt" ? "Criar Evento" : locale.language === "es" ? "Crear Evento" : "Create Event"}
            </Button>
          </div>

          {/* Filters Row */}
          <div className="flex gap-3 flex-wrap justify-center items-center">
            {/* Language Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2 bg-card border-border">
                  {selectedLanguage === "all" ? (
                    <>
                      <Globe size={16} />
                      {locale.language === "pt" ? "Todos os Idiomas" : locale.language === "es" ? "Todos los Idiomas" : locale.language === "fr" ? "Toutes les Langues" : locale.language === "de" ? "Alle Sprachen" : locale.language === "it" ? "Tutte le Lingue" : "All Languages"}
                    </>
                  ) : (
                    <>
                      <span>{eventLanguageConfigs.find(c => c.language === selectedLanguage)?.flag}</span>
                      {eventLanguageConfigs.find(c => c.language === selectedLanguage)?.name[locale.language] || eventLanguageConfigs.find(c => c.language === selectedLanguage)?.name.en}
                    </>
                  )}
                  <ChevronDown size={14} className="opacity-50" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-card border-border z-50">
                <DropdownMenuItem 
                  onClick={() => setSelectedLanguage("all")}
                  className={selectedLanguage === "all" ? "bg-primary/10 text-primary" : ""}
                >
                  <Globe size={16} className="mr-2" />
                  {locale.language === "pt" ? "Todos os Idiomas" : locale.language === "es" ? "Todos los Idiomas" : locale.language === "fr" ? "Toutes les Langues" : locale.language === "de" ? "Alle Sprachen" : locale.language === "it" ? "Tutte le Lingue" : "All Languages"}
                </DropdownMenuItem>
                {eventLanguageConfigs.map((config) => (
                  <DropdownMenuItem
                    key={config.language}
                    onClick={() => setSelectedLanguage(config.language)}
                    className={selectedLanguage === config.language ? "bg-primary/10 text-primary" : ""}
                  >
                    <span className="mr-2">{config.flag}</span>
                    {config.name[locale.language] || config.name.en}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Category Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2 bg-card border-border">
                  <Filter size={16} />
                  {selectedCategory === "all" 
                    ? (locale.language === "pt" ? "Todos Eventos" : locale.language === "es" ? "Todos Eventos" : "All Events")
                    : t(categories.find(c => c.key === selectedCategory)?.labelKey || "events.all")}
                  <ChevronDown size={14} className="opacity-50" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-card border-border z-50">
                {categories.map((cat) => (
                  <DropdownMenuItem
                    key={cat.key}
                    onClick={() => setSelectedCategory(cat.key)}
                    className={selectedCategory === cat.key ? "bg-primary/10 text-primary" : ""}
                  >
                    {cat.key === "all" 
                      ? (locale.language === "pt" ? "Todos Eventos" : locale.language === "es" ? "Todos Eventos" : "All Events")
                      : t(cat.labelKey)}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </motion.div>

        {/* Events Grid */}
        {isLoading ? (
          <EventGridSkeleton count={6} />
        ) : sortedEvents.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-muted-foreground">
              {searchQuery 
                ? t("events.noResults")
                : locale.language === "pt" 
                  ? "Nenhum evento encontrado. Seja o primeiro a criar!" 
                  : locale.language === "es"
                  ? "No se encontraron eventos. ¡Sé el primero en crear uno!"
                  : "No events found. Be the first to create one!"}
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {paginatedEvents.map((event, index) => (
                <EventCard 
                  key={event.id}
                  id={event.id}
                  title={locale.language === "pt" && event.title_pt ? event.title_pt : event.title}
                  description={locale.language === "pt" && event.description_pt ? event.description_pt : event.description}
                  deadline={event.deadline}
                  yesPool={event.yes_pool}
                  noPool={event.no_pool}
                  totalBids={event.total_bids}
                  status={event.status as "OPEN" | "CLOSED" | "RESOLVED"}
                  language={event.country as EventLanguage}
                  index={index}
                  isCreator={user?.id === event.creator_id}
                  isAdmin={isAdmin}
                  canDelete={userBidsMap[event.id] ?? false}
                  hasPendingResolution={pendingResolutionEventIds.has(event.id)}
                  pendingResolution={pendingResolutionsMap[event.id] || null}
                  userHasBid={userBidEventIds.has(event.id)}
                  onBidClick={() => handleBidClick(event)}
                  onResolveClick={() => handleResolveClick(event)}
                  onVoteClick={() => handleVoteClick(event)}
                  onDeleteClick={() => handleDeleteClick(event)}
                  isDeleting={deletingEventId === event.id}
                />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="flex items-center justify-center gap-2 mt-8"
              >
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="gap-1"
                >
                  <ChevronLeft size={16} />
                  {locale.language === "pt" ? "Anterior" : "Previous"}
                </Button>
                
                <div className="flex items-center gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter((page) => {
                      // Show first, last, current, and adjacent pages
                      return page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1;
                    })
                    .map((page, idx, arr) => (
                      <span key={page} className="flex items-center">
                        {idx > 0 && arr[idx - 1] !== page - 1 && (
                          <span className="px-2 text-muted-foreground">...</span>
                        )}
                        <Button
                          variant={currentPage === page ? "default" : "ghost"}
                          size="sm"
                          onClick={() => setCurrentPage(page)}
                          className="w-8 h-8 p-0"
                        >
                          {page}
                        </Button>
                      </span>
                    ))}
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="gap-1"
                >
                  {locale.language === "pt" ? "Próximo" : "Next"}
                  <ChevronRight size={16} />
                </Button>
              </motion.div>
            )}

            {/* Results count */}
            <div className="text-center mt-4 text-sm text-muted-foreground">
              {locale.language === "pt" 
                ? `Mostrando ${(currentPage - 1) * EVENTS_PER_PAGE + 1}-${Math.min(currentPage * EVENTS_PER_PAGE, sortedEvents.length)} de ${sortedEvents.length} eventos`
                : `Showing ${(currentPage - 1) * EVENTS_PER_PAGE + 1}-${Math.min(currentPage * EVENTS_PER_PAGE, sortedEvents.length)} of ${sortedEvents.length} events`}
            </div>
          </>
        )}
      </div>

      <CreateEventModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onEventCreated={fetchEvents}
      />

      <EventDetailModal
        isOpen={isBidModalOpen}
        onClose={() => setIsBidModalOpen(false)}
        event={selectedEvent ? {
          id: selectedEvent.id,
          title: locale.language === "pt" && selectedEvent.title_pt ? selectedEvent.title_pt : selectedEvent.title,
          description: locale.language === "pt" && selectedEvent.description_pt ? selectedEvent.description_pt : selectedEvent.description,
          yes_pool: selectedEvent.yes_pool,
          no_pool: selectedEvent.no_pool,
          total_bids: selectedEvent.total_bids,
          deadline: selectedEvent.deadline,
          status: selectedEvent.status as "OPEN" | "CLOSED" | "RESOLVED",
          language: selectedEvent.country,
        } : null}
        onBidPlaced={fetchEvents}
        prefetchedUserBid={selectedEvent ? userBidDetailsMap[selectedEvent.id] || null : null}
      />

      <ResolveEventModal
        isOpen={isResolveModalOpen}
        onClose={() => setIsResolveModalOpen(false)}
        event={selectedEvent}
        onEventResolved={fetchEvents}
      />

      <SubmitResolutionModal
        isOpen={isSubmitResolutionModalOpen}
        onClose={() => setIsSubmitResolutionModalOpen(false)}
        event={selectedEvent}
        onResolutionSubmitted={fetchEvents}
      />

      <VoteOnResolutionModal
        isOpen={isVoteModalOpen}
        onClose={() => setIsVoteModalOpen(false)}
        resolution={selectedResolution}
        eventTitle={selectedEvent?.title || ""}
        onVoteSubmitted={fetchEvents}
      />

      <ConfirmDialog
        isOpen={!!confirmDeleteEvent}
        onClose={() => setConfirmDeleteEvent(null)}
        onConfirm={confirmDelete}
        title={locale.language === "pt" ? "Excluir Evento" : "Delete Event"}
        description={
          locale.language === "pt" 
            ? "Tem certeza que deseja excluir este evento? Todas as apostas serão reembolsadas. Esta ação não pode ser desfeita."
            : "Are you sure you want to delete this event? All bids will be refunded. This action cannot be undone."
        }
        confirmText={locale.language === "pt" ? "Excluir" : "Delete"}
        cancelText={locale.language === "pt" ? "Cancelar" : "Cancel"}
        variant="destructive"
        isLoading={!!deletingEventId}
      />
    </section>
  );
});

EventsSection.displayName = "EventsSection";

export default EventsSection;
